import useAuthStore from '@/stores/AuthStore'
import { pick } from '@haina/utils-commons'
import { SearchOutlined, UnorderedListOutlined, LeftOutlined, MergeCellsOutlined, PartitionOutlined, LinkOutlined, ExpandOutlined, InfoOutlined, FileMarkdownOutlined, HistoryOutlined } from '@ant-design/icons'
import { Collapse, Flex, Spin, Input, Typography, Table, Layout, Space, Button, Tooltip, Form, Radio, Empty, App } from 'antd'
import { useEffect, useMemo, useState, useRef, isValidElement } from 'react'
import { useCustomerProfileStore } from '@/stores/customer-profile'
import { isNotEmpty, isEmpty } from '@haina/utils-commons'
import { Conditional } from '@/components/Conditional'
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const LineItem = ({ title, value, variant }) => {
  return (
    <div className={'flex justify-between items-start py-1 px-1'}>
      <Conditional
        condition={isNotEmpty(title)}
        whenTrue={
          <div className='w-24 text-left'>
            <Typography.Text type='secondary' className='after:content-[":"]'>{title}</Typography.Text>
          </div>
        }
        whenFalse={<></>}
      />
      <div className='w-full text-left'>
        <Conditional
          condition={variant === 'link'}
          whenTrue={
            <><LinkOutlined /> <a target='_blank' rel='noopener noreferrer' href={value}>{value}</a></>
          }
          whenFalse={isValidElement(value) ? value : <Typography.Text>{value ?? '-'}</Typography.Text>}
        />
      </div>
    </div>
  )
}

const SimpleMarkdown = (markdown) => {
  if (!markdown) return <div className="text-gray-500">-</div>
  const lines = markdown.split('\n')

  return (
    <div className="space-y-2 bg-white">
      {lines.map((line, index) => (
        <div key={index} className="text-sm">
          {line}
        </div>
      ))}
    </div>
  )
}

const CustomerOrderList = ({ dataSource, onClick }) => {

  const [clickedOrder, setClickedOrder] = useState(0)

  useEffect(() => {
    if (dataSource.length > 0) {
      setClickedOrder(dataSource[0])
    }
  }, [dataSource])

  return (
    <Flex gap='small' wrap className='py-2'>
      <Conditional
        condition={dataSource.length > 0}
        whenTrue={
          dataSource.map((item, _) => {
          const currentOrder = clickedOrder.order_sn === item.order_sn
          const buttonColor = currentOrder ? 'primary' : 'default'
          const buttonVariant = currentOrder ? 'filled' : 'text'
            return (
              <Button color={buttonColor} variant={buttonVariant} onClick={() => {
                setClickedOrder(item)
                if (onClick) {
                  onClick(item)
                }
              }}>
                {item.order_id}
              </Button>
            )
          })
        }
        whenFalse={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      />
    </Flex>
  )
}

function AllInOne() {

  const customerListColumns = [
    {
      title: '名字/邮箱',
      dataIndex: 'customer_name',
      render: (_, customer) => {
        return (
          <Space direction="vertical">
            <Conditional
              condition={customer.global_customer_id === selectedCustomerId}
              whenTrue={
                <Radio defaultChecked checked={true}>{customer.customer_name}</Radio>
              }
              whenFalse={<Typography.Text>{customer.customer_name}</Typography.Text>}
            />
            <Typography.Text type="secondary">{customer.customer_email}</Typography.Text>
          </Space>
        )
      }
    },
    {
      title: '订单号',
      dataIndex: 'order_id',
      width: '150px',
    },
  ]

  const [collapsed, setCollapsed] = useState(false)
  const [spinning, setSpinning] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState(0)
  const keywordRef = useRef(null)

  const { notification, message } = App.useApp();


  const [collapseProfileList, setCollapseProfileList] = useState([])

  const [
    customerList, fetchCustomerList,
    customerDetail, fetchCustomerDetail,
    contactList, fetchContactList,
    orderList, fetchOrderList,
    orderProfile, fetchOrderProfile,
    customerSummary, fetchCustomerSummary
  ] = useCustomerProfileStore((s) => [
    s.customerList, s.fetchCustomerList,
    s.customerDetail, s.fetchCustomerDetail,
    s.contactList, s.fetchContactList,
    s.orderList, s.fetchOrderList,
    s.orderProfile, s.fetchOrderProfile,
    s.customerSummary, s.fetchCustomerSummary,
  ])

  const calculateAge = (birthday) => {
    if (!birthday) return '-';

    const birthDate = new Date(birthday);

    if (isNaN(birthDate.getTime())) {
      return '-';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const customerAge = useMemo(() => {
    return calculateAge(customerDetail.birthday);
  }, [customerDetail.birthday]);

  const searchCustomerList = () => {
    const keyword = keywordRef.current.input.value
    if (isEmpty(keyword)) {
      notification.warning({ message: '请问你想搜什么？', description: '至少给我点提示吧🤣', placement: 'topLeft' });
      return
    }
    setSpinning(true)
    fetchCustomerList(keywordRef.current.input.value).then((result) => {
      console.info('searchCustomerList: ', result)
    }).finally(() => {
      setSpinning(false)
    })
  }

  const loadOrderProfile = (orderSn) => {
    fetchOrderProfile(orderSn).then((result) => {
      console.info('fetchOrderProfile: ', result)

      const getKey = (profile) => {
        if (profile.milestone === 'L1') {
          return '1'
        }
        if (profile.milestone === 'L2') {
          return '2'
        }
        if (profile.milestone === 'L3') {
          return '3'
        }
      }
      const getLabel = (profile) => {
        if (profile.milestone === 'L1') {
          return 'L1 咨询阶段'
        }
        if (profile.milestone === 'L2') {
          return 'L2 走团阶段'
        }
        if (profile.milestone === 'L3') {
          return 'L3 复盘激活'
        }
      }
      const getExtra = (profile) => {
        if (profile.milestone === 'L1') {
          return '顾问'
        }
        if (profile.milestone === 'L2') {
          return '导游'
        }
        if (profile.milestone === 'L3') {
          return '客运'
        }
      }
      const profileMapList = result.map((profile) => {
        return {
          key: getKey(profile),
          label: getLabel(profile),
          children: SimpleMarkdown(profile.insight),
          extra: (
            <>
              <span>👩‍🦱 {getExtra(profile)}</span>
            </>
          ),
        }
      })
      setCollapseProfileList(profileMapList)
    })
  }

  const loadCustomerDetail = async (customer) => {
    setSpinning(true)
    console.info('customer: ', customer)
    const customerId = customer.global_customer_id
    setSelectedCustomerId(customer.global_customer_id)

    try {
      const [detailResult, summaryResult, contactResult, orderResult] = await Promise.all([
        fetchCustomerDetail(customerId),
        fetchCustomerSummary(customerId),
        fetchContactList(customerId),
        fetchOrderList(customerId)
      ]);

      if (orderResult.length > 0) {
        await loadOrderProfile(orderResult[0].order_sn);
      } else {
        setCollapseProfileList([])
      }
      // throw new Error('获取数据失败');
    } catch (error) {
      notification.error({ message: '失败是成功之母🤣', description: error.message, placement: 'top' });
    } finally {
      setSpinning(false);
    }
  }

  const formatContactName = (type) => {
    switch (type) {
      case 'email':
        return '邮箱';
      case 'phone_number':
        return '手机';
      case 'whatsapp':
        return 'WhatsApp';
      default:
        return '未知';
    }
  }

  const formatBirthday = (dateString) => {
    if (!dateString) return '-';

    const date = dayjs(dateString);

    if (!date.isValid()) {
      return '-';
    }

    return date.format('YYYY-MM-DD');
  };

  return (
    <>
    <Spin spinning={spinning} fullscreen />
      <Layout>
        <Layout.Sider width='380' theme='light' className='relative'>
          <Typography.Title level={4}>客人列表</Typography.Title>
          <Space.Compact style={{ width: '100%' }}>
            <Input allowClear placeholder='名字、邮箱、订单号' ref={keywordRef} onPressEnter={() => {
                  searchCustomerList()
                }} />
            <Tooltip title='搜索'>
              <Button
                type='primary'
                icon={<SearchOutlined />}
                onClick={() => {
                  searchCustomerList()
                }}
              />
            </Tooltip>
          </Space.Compact>

          <Table
            rowKey='global_customer_id'
            pagination={{
              pageSize: 10,
              showQuickJumper: false,
              showLessItems: false,
              showSizeChanger: false,
              simple: { readOnly: true },
              size: "small",
              position: ['topLeft'],
            }}
            columns={customerListColumns}
            dataSource={customerList}
            onRow={(customer) => {
              return {
                onClick: () => {
                  loadCustomerDetail(customer)
                },
              }
            }}
          />
        </Layout.Sider>
        <Layout.Content style={{ minWidth: '360px' }} className='bg-white px-5'>
          <Typography.Title level={4}>✨ 客人小结</Typography.Title>

            {isEmpty(customerSummary.insight) ? <Flex><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Flex> : <div className='space-y-2 pb-4 border-solid border-0 border-b border-gray-200'>
              {customerSummary.insight.split('\n').map((line, index) => (
                  <div key={index} className="text-sm">
                    {line}
                  </div>
                ))}

              <Flex justify='flex-end' align='center'>
              <Button type='text' icon={<InfoOutlined />} onClick={() => {
                message.info('没有')
              }}>提示词</Button>
              <Button type='text' icon={<FileMarkdownOutlined />} onClick={() => {
                message.info('没有')
              }}>参考资料(md)</Button>
              <Button type='text' icon={<HistoryOutlined />}>{dayjs(customerSummary.updated_time).format('YYYY-MM-DD hh:mm')}</Button>
            </Flex>
              </div>}

          <Typography.Title level={5}>所有订单</Typography.Title>
          <CustomerOrderList dataSource={orderList} onClick={(order) => {
            console.info('click order: ', order)
            loadOrderProfile(order.order_sn)
          }} />

          <Typography.Title level={5}>✨ 客户旅程</Typography.Title>

          <Conditional
            condition={collapseProfileList.length > 0}
            whenTrue={
              <Collapse
                items={collapseProfileList}
                defaultActiveKey={['1', '2', '3']}
                bordered={false}
                // ghost
              />
            }
            whenFalse={<Flex><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Flex>}
          />

        </Layout.Content>

        <Tooltip title={collapsed ? '展开' : '收起'} placement='left'>
          <Button
            icon={collapsed ? <LeftOutlined /> : <UnorderedListOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className={`absolute z-10 rounded-none  ${collapsed ? 'right-1 top-20 rounded-l-xl' : 'right-8 top-20 rounded-l'}`}
            size={collapsed ? 'small' : 'middle'}
          />
        </Tooltip>
        <Layout.Sider
          width='400'
          theme='light'
          className='overflow-y-auto'
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          collapsedWidth={0}
          trigger={null}
          reverseArrow={true}>
          <Typography.Title level={4}>客人档案</Typography.Title>

          <div className='bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200'>

            <LineItem title='名字' value={(customerDetail.first_name??'-') + ' ' + (customerDetail.last_name??'-')} />
            <LineItem title='性别' value={customerDetail.gender} />
            <LineItem title='年龄' value={customerAge} />
            <LineItem title='生日' value={formatBirthday(customerDetail.birthday)} />
            <LineItem title='国籍' value={customerDetail.country} />
            <Flex justify='flex-end'>
              <Button type='text' icon={<ExpandOutlined />} onClick={() => {
                message.info('没有更多了')
              }}>更多</Button>
            </Flex>
          </div>
          <br />

          <Typography.Title level={5}>联系方式</Typography.Title>

          <div className='bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200'>
            {contactList.map((item, index) => {
                return (
                  <LineItem key={index} title={formatContactName(item.contact_type)}
                  value={
                    <Button type='link' size='small'>{item.contact_value}</Button>
                  }/>
                )
              })}
            <Flex justify='flex-end'>
              <Button type='text' icon={<MergeCellsOutlined />} onClick={() => {
                message.info('合不了')
              }}>合并</Button>
              <Button type='text' icon={<PartitionOutlined />} onClick={() => {
                message.info('没法拆')
              }}>拆分</Button>
            </Flex>
          </div>

          <br />

          <Typography.Title level={5}>社媒</Typography.Title>
          <div className='bg-white p-1 max-w-4xl mx-auto'>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          {/* <LineItem variant='link' value='https://www.facebook.com/jennifergonzalon' />
          <LineItem variant='link' value='https://www.linkedin.cn/jennifergonzalon' />
          <LineItem variant='link' value='https://www.youtube.com/@jennifergonzalon' />
          <LineItem variant='link' value='https://www.twitter.com/jennifergonzalon' /> */}
          </div>

        </Layout.Sider>
      </Layout>
    </>
  )
}

export default AllInOne
